import classNames from 'classnames';
import _ from 'lodash';
import React, { useMemo, useRef } from 'react';
import type { MentionsInputProps } from 'react-mentions';
import { Mention, MentionsInput } from 'react-mentions';
import { useDispatch } from 'react-redux';
import useAppSelector from '../app/redux/useAppSelector';
import BorderedIcon from '../components/BorderedIcon';
import IconButton from '../components/IconButton';
import SelectEntityOption from '../components/SelectEntityOption';
import SVGIcon from '../components/SVGIcon';
import type {
  PostSelectOption_PostFragment,
  TopicSelectOption_TopicFragment,
  UserSelectOption_UserFragment,
} from '../graphql/generated/graphqlTypes';
import { PostType } from '../graphql/generated/graphqlTypes';
import GroupSelectOption from '../groups/GroupSelectOption';
import useAllGroups from '../groups/useAllGroups';
import SearchOptionsModal from '../help/SearchOptionsModal';
import informationIcon from '../icons/prefilled/information.svg';
import publicIcon from '../icons/public.svg';
import searchIcon from '../icons/search.svg';
import { PostIconForState } from '../post/PostIcon';
import PostSelectOption from '../post/PostSelectOption';
import VerifiedIcon from '../post/VerifiedIcon';
import type {
  GroupEntity,
  PostEntity,
  TopicEntity,
  UserEntity,
} from '../search/entitySearch/localEntitySearch';
import localEntitySearch, {
  userToEntity,
} from '../search/entitySearch/localEntitySearch';
import useAllPostsForEntitySearch from '../search/entitySearch/useAllPostsForEntitySearch';
import TopicSelectOption from '../topic/TopicSelectOption';
import useAllTopics from '../topic/useAllTopics';
import UIActions from '../ui/UIActions';
import useAllUsers from '../user/useAllUsers';
import UserSelectOption from '../user/UserSelectOption';
import styles from './FacetedSearchInput.module.scss';
import { facets, postStateData } from './facets';

const moveEntityToTop = <E extends { id: string }>(
  entity: E,
  originalEntities: E[],
): E[] => {
  const entities = [...originalEntities];
  _.remove(entities, ({ id }) => id === entity.id);
  entities.unshift(entity);
  return entities;
};

interface FacetedSearchInputProps extends Omit<MentionsInputProps, 'children'> {
  slabForwardedRef: React.ForwardedRef<HTMLInputElement>;
  expanded?: boolean;
  asSearchOption?: boolean;
}

const FacetedSearchInput = ({
  slabForwardedRef,
  expanded,
  asSearchOption,
  ...inputProps
}: FacetedSearchInputProps) => {
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const currentUser = useAppSelector((state) => state.session.user);

  const showSearchOptions = () => {
    dispatch(UIActions.openModal(SearchOptionsModal));
  };

  const topics = useAllTopics({ flatten: true });
  const users = useAllUsers();
  const posts = useAllPostsForEntitySearch();
  const groups = useAllGroups().data;

  const objectsForType = useMemo(
    () => ({
      topic: topics,
      user: users,
      postState: postStateData,
      post: posts,
      group: groups,
      mention: [...users, ...posts, ...topics, ...groups],
    }),
    [groups, posts, topics, users],
  );

  const localSearch = (
    query: string,
    types: Array<keyof typeof objectsForType>,
  ) =>
    localEntitySearch(
      query,
      Object.fromEntries(
        types.map((type) => [type, { objects: objectsForType[type] }]),
      ),
      { limit: 25 },
    );

  const propsForType = {
    topic: {
      data: (query: string) => localSearch(query, ['topic']),
      renderSuggestion: (
        entry: TopicEntity<TopicSelectOption_TopicFragment>,
        _search: string,
        _highlightedDisplay: React.ReactNode,
        _index: number,
        focused: boolean,
      ) => <TopicSelectOption {...entry} focused={focused} />,
    },
    user: {
      data: (query: string) => {
        const entities = localSearch(query, ['user']);
        return query.trim() === 'me'
          ? moveEntityToTop(userToEntity(currentUser), entities)
          : entities;
      },
      renderSuggestion: (entry: UserEntity<UserSelectOption_UserFragment>) => (
        <UserSelectOption {...entry} />
      ),
    },
    mention: {
      data: (query: string) => {
        const entities = localSearch(query, ['user', 'post', 'group', 'topic']);
        return query.trim() === 'me'
          ? moveEntityToTop(userToEntity(currentUser), entities)
          : entities;
      },
      renderSuggestion: (
        entry:
          | UserEntity<UserSelectOption_UserFragment>
          | PostEntity<PostSelectOption_PostFragment>
          | GroupEntity<{ id: string; name: string }>
          | TopicEntity<TopicSelectOption_TopicFragment>,
        _search: string,
        _highlightedDisplay: React.ReactNode,
        _index: number,
        focused: boolean,
      ) => {
        switch (entry.type) {
          case 'post':
            return <PostSelectOption {...entry} focused={focused} />;
          case 'user':
            return <UserSelectOption {...entry} />;
          case 'topic':
            return <TopicSelectOption {...entry} focused={focused} />;
          case 'group':
            return <GroupSelectOption {...entry} focused={focused} />;
          default:
            return null;
        }
      },
    },
    postState: {
      data: postStateData,
      renderSuggestion: (
        entry: typeof postStateData[number],
        _search: string,
        highlightedDisplay: React.ReactNode,
        _index: number,
        focused: boolean,
      ) => {
        let icon;
        switch (entry.id) {
          case 'expired':
            icon = <VerifiedIcon bordered variant="warning" />;
            break;
          case 'public':
            icon = <BorderedIcon>{publicIcon}</BorderedIcon>;
            break;
          case 'verified':
            icon = <VerifiedIcon bordered variant="success" />;
            break;
          default:
            icon = (
              <PostIconForState
                state={{
                  type: PostType.Post,
                  draft: entry.id === 'draft',
                  archived: entry.id === 'archived',
                }}
                prefilled={entry.id === 'published'}
                bordered={focused}
              />
            );
        }
        return <SelectEntityOption icon={icon} label={highlightedDisplay} />;
      },
    },
  };

  return (
    <label
      className={classNames(styles.container, {
        [styles.expanded]: expanded,
        [styles.asSearchOption]: asSearchOption,
      })}
      ref={containerRef}
    >
      {!asSearchOption && (
        <SVGIcon className={styles.icon}>{searchIcon}</SVGIcon>
      )}
      <MentionsInput
        classNames={styles}
        inputRef={slabForwardedRef}
        singleLine
        suggestionsPortalHost={containerRef.current ?? undefined}
        readOnly={asSearchOption}
        {...inputProps}
      >
        {facets.map(({ type, prefix, prefixProps }) => (
          <Mention
            key={prefix}
            appendSpaceOnAdd
            className={styles.mention}
            {...propsForType[type]}
            // @ts-expect-error Mention does not support generic entry for renderSuggestion function
            renderSuggestion={propsForType[type].renderSuggestion}
            {...prefixProps}
            displayTransform={(id) => {
              const object = (
                objectsForType as Record<
                  keyof typeof objectsForType,
                  Array<
                    | { id: string; name: string; title?: never }
                    | { id: string; name?: never; title: string }
                  >
                >
              )[type].find((o) => o.id === id);

              return `  ${prefix}: ${
                object
                  ? object.name ?? object.title
                  : `(unknown ${_.lowerCase(type)})`
              }  `;
            }}
          />
        ))}
      </MentionsInput>
      <IconButton
        className={classNames(styles.infoIcon, { [styles.shown]: expanded })}
        onClick={showSearchOptions}
      >
        {informationIcon}
      </IconButton>
    </label>
  );
};

export default React.forwardRef<
  HTMLInputElement,
  Omit<FacetedSearchInputProps, 'slabForwardedRef'>
>((props, ref) => <FacetedSearchInput {...props} slabForwardedRef={ref} />);
